import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';
import useSEO from '../hooks/useSEO';

const { FiArrowRight, FiClock, FiZap } = FiIcons;

const InsightsHub = () => {
  const { lang } = useLanguage();
  
  useSEO(
    lang === 'es' ? "Insights | Ideas Prácticas de Adquisición de Clientes | Mathias Deppe" : "Insights | Practical Customer Acquisition Ideas | Mathias Deppe",
    lang === 'es' ? "Ideas prácticas para convertir la atención en clientes. Explora insights de crecimiento para gimnasios, clínicas de estética y servicios B2B." : "Practical ideas for turning attention into customers. Explore growth insights for gyms, beauty clinics, and B2B services."
  );

  const ui = {
    read: lang === 'es' ? "Leer insight" : "Read insight",
    minRead: lang === 'es' ? "min de lectura" : "min read",
    featured: lang === 'es' ? "DESTACADO" : "FEATURED",
    categories: {
      gym: lang === 'es' ? "CRECIMIENTO DE GIMNASIOS" : "GYM GROWTH",
      beauty: lang === 'es' ? "CRECIMIENTO ESTÉTICO" : "BEAUTY GROWTH",
      acq: lang === 'es' ? "ADQUISICIÓN DE CLIENTES" : "CUSTOMER ACQUISITION"
    }
  };

  const insights = [
    {
      id: 'gym-lose-leads',
      category: ui.categories.gym,
      title: lang === 'es' ? "5 Razones por las que los Gimnasios de Barcelona Pierden Leads en Instagram" : "5 Reasons Barcelona Gyms Lose Instagram Leads",
      excerpt: lang === 'es' ? "La mayoría de los gimnasios no tienen un problema de contenido. Tienen un problema de conversión. Aquí es donde desaparecen los leads entre el primer DM y la reserva." : "Most gyms don't have a content problem. They have a conversion problem. Here's where leads disappear between the first DM and the trial booking.",
      readTime: 6,
      image: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&q=80&w=1000",
      featured: true,
      path: "/insights/5-reasons-gyms-lose-instagram-leads"
    },
    {
      id: 'speed-to-lead',
      category: ui.categories.acq,
      title: lang === 'es' ? "Matemáticas del 'Speed-to-Lead': Por qué 5 Minutos es la Zona de Muerte" : "The 'Speed-to-Lead' Math: Why 5 Minutes is the Kill Zone",
      readTime: 4,
      path: "/insights/speed-to-lead-math"
    },
    {
      id: 'beauty-outcomes',
      category: ui.categories.beauty,
      title: lang === 'es' ? "Clínicas de Estética: Deja de Vender Tratamientos, Empieza a Vender Resultados" : "Beauty Clinics: Stop Selling Treatments, Start Selling Outcomes",
      readTime: 5,
      path: "/insights/beauty-outcomes-vs-treatments"
    },
    {
      id: 'database-reactivation',
      category: ui.categories.acq,
      title: lang === 'es' ? "Reactivación de Base de Datos: Los 1.000€ Ocultos en tus Listas Antiguas" : "Database Reactivation: The €1,000 Hidden in Your Old Sheets",
      readTime: 7,
      path: "/insights/database-reactivation-strategy"
    },
    {
      id: 'ai-scripting',
      category: ui.categories.acq,
      title: lang === 'es' ? "Cómo Guionizar Agentes de IA para que no Parezcan Robots" : "How to Script AI Agents That Don't Sound Like Robots",
      readTime: 5,
      path: "/insights/scripting-ai-agents"
    },
    {
      id: 'gym-revenue-challenge',
      category: ui.categories.gym,
      title: lang === 'es' ? "El Desafío de 6 Semanas: Escalando Ingresos sin Inversión en Ads" : "The 6-Week Challenge: Scaling Gym Revenue Without Ad Spend",
      readTime: 8,
      path: "/insights/6-week-gym-challenge"
    },
    {
      id: 'attributed-revenue',
      category: ui.categories.acq,
      title: lang === 'es' ? "Ingresos Atribuidos: Cómo Rastrear Ventas por DM Correctamente" : "Attributed Revenue: How to Track DM Sales Correcty",
      readTime: 4,
      path: "/insights/tracking-dm-sales"
    },
    {
      id: 'content-qualifies',
      category: ui.categories.acq,
      title: lang === 'es' ? "Contenido que Cualifica: Usando Reels para Filtrar Curiosos" : "Content That Qualifies: Using Reels to Filter Tire-Kickers",
      readTime: 6,
      path: "/insights/qualifying-content-strategy"
    }
  ];

  const featured = insights.find(i => i.featured);
  const gridItems = insights.filter(i => !i.featured);

  return (
    <div className="bg-zinc-950 text-white min-h-screen pt-32 pb-24 px-6 selection:bg-orange-500/30">
      <div className="max-w-6xl mx-auto">
        <header className="mb-20">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="text-5xl md:text-8xl font-black tracking-tighter uppercase mb-6">
              Insights<span className="text-orange-500">.</span>
            </h1>
            <p className="text-xl md:text-2xl text-zinc-400 font-medium max-w-2xl">
              {lang === 'es' ? "Ideas prácticas para convertir la atención en clientes." : "Practical ideas for turning attention into customers."}
            </p>
          </motion.div>
        </header>

        {featured && (
          <section className="mb-24">
            <Link to={featured.path} className="group relative block overflow-hidden rounded-[3rem] bg-zinc-900 border border-white/10">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="aspect-video lg:aspect-auto relative overflow-hidden">
                  <img src={featured.image} alt={featured.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/60 to-transparent lg:hidden" />
                </div>
                <div className="p-8 md:p-16 flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="px-3 py-1 bg-orange-500 text-white text-[10px] font-black tracking-widest rounded-full">
                      {ui.featured}
                    </span>
                    <span className="text-zinc-500 text-xs font-bold uppercase tracking-widest">
                      {featured.category}
                    </span>
                  </div>
                  <h2 className="text-3xl md:text-5xl font-black tracking-tighter uppercase mb-6 group-hover:text-orange-500 transition-colors">
                    {featured.title}
                  </h2>
                  <p className="text-zinc-400 text-lg mb-8 line-clamp-3">
                    {featured.excerpt}
                  </p>
                  <div className="flex items-center gap-6">
                    <span className="text-white font-black uppercase tracking-widest text-sm flex items-center gap-2">
                      {ui.read} <SafeIcon icon={FiArrowRight} />
                    </span>
                    <span className="text-zinc-600 text-sm font-bold flex items-center gap-2">
                      <SafeIcon icon={FiClock} /> {featured.readTime} {ui.minRead}
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          </section>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {gridItems.map((item, i) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
            >
              <Link to={item.path} className="group flex flex-col h-full p-8 bg-zinc-900/50 border border-white/5 rounded-[2.5rem] hover:border-orange-500/30 transition-all">
                <div className="flex items-center justify-between mb-6">
                  <span className="text-orange-500 text-[10px] font-black tracking-widest uppercase">
                    {item.category}
                  </span>
                  <SafeIcon icon={FiArrowRight} className="text-zinc-700 group-hover:text-orange-500 transition-colors" />
                </div>
                <h3 className="text-2xl font-black tracking-tighter uppercase mb-8 group-hover:text-white transition-colors text-zinc-300 flex-1">
                  {item.title}
                </h3>
                <div className="flex items-center justify-between pt-6 border-t border-white/5">
                  <span className="text-zinc-600 text-xs font-bold flex items-center gap-2">
                    <SafeIcon icon={FiClock} /> {item.readTime} {ui.minRead}
                  </span>
                  <div className="w-8 h-8 rounded-full bg-zinc-950 flex items-center justify-center border border-white/5">
                    <SafeIcon icon={FiZap} className="text-xs text-orange-500" />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default InsightsHub;